import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    TextField,
    MenuItem,
} from '@mui/material';
import { Auth } from 'aws-amplify';
import CentralCalls from '../../centralCalls';
import FileUpload from '../FileUpload';

/**
 * PostFinishJobAutomation Component
 * 
 * Handles the logic and dialogs that appear after a job is marked as finished.
 * Takes in a row (phase status) and routes to appropriate dialog based on conditions:
 * 
 * Route 1: QC or Inspection phases -> handles slab updates, QC/Inspection status updates,
 *          auto-punch creation for failed QC, and setout cancellation
 * Route 2: Finished with status 'fail' (non-QC/non-Inspection) -> opens phase status dialog
 *          to collect comments, date_scheduled, and employee_team_oid, then inserts phase_status
 * Route 3: All other cases -> completes immediately without any dialog
 */

function PostFinishJobAutomation({ row, lotOid, onComplete, open = false, onClose }) {
    // Failed QC dialog state
    const [failedQCDialogOpen, setFailedQCDialogOpen] = useState(false);
    const [failedQCComments, setFailedQCComments] = useState('');
    const [failedQCDate, setFailedQCDate] = useState('');
    const [failedQCPhaseRow, setFailedQCPhaseRow] = useState(null);
    const [failedQCTeams, setFailedQCTeams] = useState([]);
    const [failedQCTeamOid, setFailedQCTeamOid] = useState('');
    const [failedQCPoNumber, setFailedQCPoNumber] = useState('');
    const [failedQCCost, setFailedQCCost] = useState('');
    const [failedQCIsInspection, setFailedQCIsInspection] = useState(false);
    const [failedQCInspectionDate, setFailedQCInspectionDate] = useState('');
    const failedQCFilesRef = React.useRef(null);

    // New Phase Status dialog state
    const [newPhaseStatusDialogOpen, setNewPhaseStatusDialogOpen] = useState(false);
    const [newPhaseStatusComments, setNewPhaseStatusComments] = useState('');
    const [newPhaseStatusDateScheduled, setNewPhaseStatusDateScheduled] = useState('');
    const [newPhaseStatusPhaseStatusRow, setNewPhaseStatusPhaseStatusRow] = useState(null);
    const [newPhaseStatusTeams, setNewPhaseStatusTeams] = useState([]);
    const [newPhaseStatusTeamOid, setNewPhaseStatusTeamOid] = useState('');
    const [newPhaseStatusInsertedOid, setNewPhaseStatusInsertedOid] = useState(null);
    const [newPhaseStatusPutOnHold, setNewPhaseStatusPutOnHold] = useState(false);
    const newPhaseStatusFilesRef = React.useRef(null);

    // Cover Sewer dialog state (underground + complete)
    const [coverSewerDialogOpen, setCoverSewerDialogOpen] = useState(false);
    const [coverSewerRequestedDate, setCoverSewerRequestedDate] = useState('');
    const [coverSewerTeams, setCoverSewerTeams] = useState([]);
    const [coverSewerTeam, setCoverSewerTeam] = useState('');
    const coverSewerFilesRef = React.useRef(null);
    const [uploadUserName, setUploadUserName] = useState('unknown');
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const coverSewerInFlightRef = React.useRef(false);
    const coverSewerHandledTriggerRef = React.useRef(null);
    // Track in-progress inserts by phase_oid to avoid duplicate concurrent inserts
    const insertingPhaseMapRef = React.useRef(new Map());

    const getNumericPhaseOid = (value) => {
        const num = Number(value);
        return Number.isFinite(num) && num > 0 ? num : null;
    };

    const isRetryablePhaseStatusInsertError = (err) => {
        const code = err?.code;
        const message = String(err?.message || '').toLowerCase();
        return (
            code === 'ER_LOCK_DEADLOCK' ||
            code === 'ER_LOCK_WAIT_TIMEOUT' ||
            code === 'ER_QUERY_INTERRUPTED' ||
            message.includes('deadlock') ||
            message.includes('lock wait timeout')
        );
    };

    // Fetch employee teams when the Failed QC dialog opens
    useEffect(() => {
        const fetchTeams = async () => {
            if (!failedQCDialogOpen) return;
            try {
                const rows = await CentralCalls.fetchTableData('employee_team', {});
                setFailedQCTeams(rows || []);
            } catch (err) {
                console.error('Error fetching teams', err);
                setFailedQCTeams([]);
            }
        };
        fetchTeams();
    }, [failedQCDialogOpen]);

    // Fetch employee teams when the New Phase Status dialog opens
    useEffect(() => {
        const fetchTeams = async () => {
            if (!newPhaseStatusDialogOpen) return;
            try {
                const rows = await CentralCalls.fetchTableData('employee_team', {});
                setNewPhaseStatusTeams(rows || []);
            } catch (err) {
                console.error('Error fetching teams', err);
                setNewPhaseStatusTeams([]);
            }
        };
        fetchTeams();
    }, [newPhaseStatusDialogOpen]);

    // Fetch employee teams when the Cover Sewer dialog opens
    useEffect(() => {
        const fetchTeams = async () => {
            if (!coverSewerDialogOpen) return;
            try {
                const rows = await CentralCalls.fetchTableData('employee_team', {});
                setCoverSewerTeams(rows || []);
            } catch (err) {
                console.error('Error fetching cover sewer teams', err);
                setCoverSewerTeams([]);
            }
        };
        fetchTeams();
    }, [coverSewerDialogOpen]);

    useEffect(() => {
        const setUserInfo = async () => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                setUploadUserName(user?.attributes?.email || user?.username || 'unknown');
            } catch (error) {
                console.error('Error fetching user info', error);
                setUploadUserName('unknown');
            }
        };

        setUserInfo();
    }, []);

    const uploadFilesForPhase = async (filesToUpload, phaseOid, uploadLotOid) => {
        if (!filesToUpload || filesToUpload.length === 0 || !phaseOid || !uploadLotOid) {
            return;
        }

        setIsUploadingFiles(true);
        try {
            for (const file of filesToUpload) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('lot_oid', uploadLotOid);
                formData.append('uploaded_by', uploadUserName || 'unknown');

                const response = await fetch('/api/files/upload', {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Upload failed for ${file.name}`);
                }

                const uploadedRow = await response.json();
                if (uploadedRow?.oid) {
                    await CentralCalls.upsertData(
                        { oid: uploadedRow.oid, phase_oid: phaseOid },
                        'files',
                        'update'
                    );
                }
            }
        } finally {
            setIsUploadingFiles(false);
        }
    };

    const forceInsertMissingPhaseStatus = async (phaseOid) => {
        const normalizedPhaseOid = getNumericPhaseOid(phaseOid);
        if (!normalizedPhaseOid) return false;

        try {
            await CentralCalls.callStoredProcedureWithParams('sp_forceInsertMissingPhaseStatus', { phase_oid: normalizedPhaseOid });
            return true;
        } catch (forceErr) {
            console.error('Error forcing missing phase_status insert:', forceErr);
            return false;
        }
    };

    const getLatestActivePhaseStatus = async (phaseOid) => {
        const normalizedPhaseOid = getNumericPhaseOid(phaseOid);
        if (!normalizedPhaseOid) return null;

        try {
            const rows = await CentralCalls.fetchTableData('phase_status', { phase_oid: normalizedPhaseOid, deleted: 0 });
            if (!Array.isArray(rows) || rows.length === 0) return null;

            return rows
                .slice()
                .sort((a, b) => {
                    const aDate = a?.create_date ? new Date(a.create_date).getTime() : 0;
                    const bDate = b?.create_date ? new Date(b.create_date).getTime() : 0;
                    if (bDate !== aDate) return bDate - aDate;
                    return (Number(b?.oid) || 0) - (Number(a?.oid) || 0);
                })[0] || null;
        } catch (err) {
            console.error('Error fetching latest phase_status:', err);
            return null;
        }
    };

    const insertPhaseStatusWithRecovery = async (insertObj) => {
        const phaseOid = getNumericPhaseOid(insertObj?.phase_oid);
        if (!phaseOid) {
            throw new Error('phase_oid is required to insert phase_status');
        }

        const maxAttempts = 5;
        let lastErr = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const inserted = await CentralCalls.upsertData(insertObj, 'phase_status', 'insert');
                if (inserted?.oid) {
                    return inserted;
                }

                throw new Error('phase_status insert did not return an oid');
            } catch (err) {
                lastErr = err;
                if (isRetryablePhaseStatusInsertError(err) && attempt < maxAttempts) {
                    const backoff = Math.min(1000, 100 * Math.pow(2, attempt - 1));
                    const jitter = Math.floor(Math.random() * 125);
                    await new Promise((resolve) => setTimeout(resolve, backoff + jitter));
                    continue;
                }
                break;
            }
        }

        await forceInsertMissingPhaseStatus(phaseOid);

        const recovered = await getLatestActivePhaseStatus(phaseOid);
        if (recovered?.oid) {
            return recovered;
        }

        try {
            const fallbackInserted = await CentralCalls.upsertData(
                { phase_oid: phaseOid, status: 'open' },
                'phase_status',
                'insert'
            );
            if (fallbackInserted?.oid) {
                return fallbackInserted;
            }
        } catch (fallbackErr) {
            console.error('Fallback phase_status insert failed:', fallbackErr);
        }

        throw lastErr || new Error('Unable to create or recover phase_status row');
    };

    // Update slab location in lot table - always runs regardless of completion status
    const updateSlabLocation = async () => {
        if (row?.phase_type === 'slab') {
            const updateObj = {
                oid: lotOid,
                sewer_location: row.sewer_location,
                water_location: row.water_location,
            };
            try {
                const response = await fetch('/api/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ tableName: 'lot', updateObj: updateObj }),
                });
                await response.json();
            } catch (err) {
                console.error(err);
            }
        }
    };

    // Main automation logic - handles phase status updates and initiates failed QC flow if needed
    const handlePostFinishLogic = async () => {
        if (!row) return;

        console.log('handling phase close...', row);

        // Handle QC or Inspection status update
        if (row.is_qc === 1 || row.is_inspection === 1) {
            try {
                await CentralCalls.upsertData({ 'oid': row.phase_oid, 'qc_status': row.qc_status }, 'phase', 'update');
            } catch (err) {
                console.error(err);
            }
        }

        // Refresh if not a failure status
        if (row.status !== 'fail' && row.qc_status !== 'fail') {
            if (onComplete) onComplete();
        }

        // Handle Failed QC -> auto-punch flow (includes inspection within same dialog)
        if ((row.is_qc === 1 || row.is_inspection === 1) && row.qc_status === 'fail') {
            console.log('Initiating failed QC auto-punch flow');
            await failedQCAutoPunch(row);
            return; // Stop here and wait for failed QC dialog
        }

        console.log('phaseRow...', row);

        // Cancel other jobs if setout is complete
        if (row.phase_type === 'setout_qc' && row.qc_status === 'success') {
            console.log('Cancelling other jobs for lot...', row, 'lotOid', lotOid);
            try {
                
                await CentralCalls.callStoredProcedureWithParams('sp_CancelOpenJobsPerLot', { lot_oid: lotOid });
                if (onComplete) onComplete();
            } catch (err) {
                console.error(err);
            }
        }
    };

    // Open the failed QC auto-punch dialog with initial data
    const failedQCAutoPunch = async (row) => {
        setFailedQCPhaseRow(row);
        setFailedQCComments(row.comments || '');
        setFailedQCIsInspection(row.is_inspection === 1);

        const dateVal = CentralCalls.normalizeDateOnly(row.date_scheduled) || row.date_scheduled || '';
        setFailedQCDate(dateVal);
        setFailedQCInspectionDate('');
        setFailedQCDialogOpen(true);
    };



    // Open the new phase status dialog with initial data
    const confirmNewPhaseStatus = async (phaseStatusRow) => {
        console.log('confirmNewPhaseStatus for row:', phaseStatusRow);
        try {
            // Insert basic phase_status row first, with retry on deadlock
            const insertObj = {
                phase_oid: getNumericPhaseOid(phaseStatusRow?.phase_oid || phaseStatusRow?.oid),
                comments: phaseStatusRow.comments || null,
                employee_oids: phaseStatusRow.employee_oids ? JSON.stringify(phaseStatusRow.employee_oids) : null
            };
            const phaseOid = insertObj.phase_oid;

            if (!phaseOid) {
                throw new Error('Missing phase_oid for failed phase follow-up status insert');
            }

            // If an insert for this phase_oid is already in progress, wait for it and reuse the result
            if (insertingPhaseMapRef.current.has(phaseOid)) {
                try {
                    const existingInserted = await insertingPhaseMapRef.current.get(phaseOid);
                    setNewPhaseStatusInsertedOid(existingInserted.oid);
                    setNewPhaseStatusPhaseStatusRow(phaseStatusRow);
                    setNewPhaseStatusComments(phaseStatusRow.comments || '');
                    setNewPhaseStatusDateScheduled('');
                    setNewPhaseStatusTeamOid(phaseStatusRow.employee_team_oid ? String(phaseStatusRow.employee_team_oid) : '');
                    setNewPhaseStatusDialogOpen(true);
                    return;
                } catch (e) {
                    // fallthrough to create a new insert attempt
                    console.error('Error waiting for existing insert:', e);
                    //await forceInsertMissingPhaseStatus(phaseOid);
                }
            }

            let resolveInsert;
            const insertPromise = new Promise((resolve, reject) => { resolveInsert = { resolve, reject }; });
            insertingPhaseMapRef.current.set(phaseOid, insertPromise);

            try {
                const inserted = await insertPhaseStatusWithRecovery(insertObj);
                resolveInsert.resolve(inserted);

                if (inserted && inserted.oid) {
                    setNewPhaseStatusInsertedOid(inserted.oid);

                    // Set up the dialog with initial data
                    setNewPhaseStatusPhaseStatusRow(phaseStatusRow);
                    setNewPhaseStatusComments(phaseStatusRow.comments || '');
                    setNewPhaseStatusDateScheduled('');
                    setNewPhaseStatusTeamOid(phaseStatusRow.employee_team_oid ? String(phaseStatusRow.employee_team_oid) : '');
                    setNewPhaseStatusDialogOpen(true);
                } else {
                    throw new Error('Insert did not return an oid');
                }
            } catch (err) {
                resolveInsert.reject(err);
                throw err;
            } finally {
                // clean up in-progress map
                insertingPhaseMapRef.current.delete(phaseOid);
            }
        } catch (err) {
            console.error('Error inserting phase_status after retries:', err);
            await forceInsertMissingPhaseStatus(phaseStatusRow?.phase_oid || phaseStatusRow?.oid);
            // If insert fails, do not open the dialog
            if (onComplete) onComplete(); // Proceed without dialog
        }
    };

    // Confirm and submit the new phase status
    const submitNewPhaseStatus = async () => {
        if (!newPhaseStatusInsertedOid) return;
        const selectedFiles = newPhaseStatusFilesRef.current?.getFiles() || [];
        
        try {
            // Update phase table if date_scheduled is provided
            if (newPhaseStatusDateScheduled) {
                const phaseOid = getNumericPhaseOid(newPhaseStatusPhaseStatusRow?.phase_oid || newPhaseStatusPhaseStatusRow?.oid);
                if (!phaseOid) {
                    throw new Error('Missing phase_oid while updating scheduled date');
                }

                await CentralCalls.upsertData(
                    {
                        oid: phaseOid,
                        date_scheduled: newPhaseStatusDateScheduled
                        //builder_date: newPhaseStatusDateScheduled
                    },
                    'phase',
                    'update'
                );
            }

            // Always update the inserted phase_status row
            const updateObj = {
                oid: newPhaseStatusInsertedOid,
                comments: newPhaseStatusComments || null
            };

            // Add employee_team_oid if provided
            if (newPhaseStatusTeamOid) {
                updateObj.employee_team_oid = parseInt(newPhaseStatusTeamOid, 10);
            }

            // Set status to 'hold' if Put On Hold is checked
            if (newPhaseStatusPutOnHold) {
                updateObj.status = 'hold';
            }

            await CentralCalls.upsertData(updateObj, 'phase_status', 'update');

            const uploadPhaseOid = getNumericPhaseOid(newPhaseStatusPhaseStatusRow?.phase_oid || newPhaseStatusPhaseStatusRow?.oid);
            if (selectedFiles.length > 0 && uploadPhaseOid) {
                try {
                    const uploadLotOid = newPhaseStatusPhaseStatusRow?.lot_oid || lotOid;
                    await uploadFilesForPhase(selectedFiles, uploadPhaseOid, uploadLotOid);
                } catch (uploadErr) {
                    console.error('Error uploading phase status files:', uploadErr);
                    alert(uploadErr?.message || 'Phase status saved, but one or more files failed to upload.');
                }
            }

            // Close and cleanup
            setNewPhaseStatusDialogOpen(false);
            setNewPhaseStatusPhaseStatusRow(null);
            setNewPhaseStatusComments('');
            setNewPhaseStatusDateScheduled('');
            setNewPhaseStatusTeamOid('');
            setNewPhaseStatusTeams([]);
            setNewPhaseStatusInsertedOid(null);
            setNewPhaseStatusPutOnHold(false);
            newPhaseStatusFilesRef.current?.clearFiles();

            if (onComplete) onComplete();
        } catch (err) {
            console.error('Error in submitNewPhaseStatus:', err);
        }
    };

    // Open Cover Sewer dialog without creating records yet
    const openCoverSewerDialog = () => {
        setCoverSewerRequestedDate('');
        setCoverSewerTeam('');
        coverSewerFilesRef.current?.clearFiles();
        setCoverSewerDialogOpen(true);
    };

    // Submit Cover Sewer form and create records only when user confirms
    const submitCoverSewer = async () => {
        if (!lotOid || !coverSewerRequestedDate) return;
        const selectedFiles = coverSewerFilesRef.current?.getFiles() || [];

        let insertedPhase = null;
        try {
            const teamVal = coverSewerTeam ? parseInt(coverSewerTeam, 10) : null;

            const phaseInsert = {
                lot_oid: lotOid,
                phase_type: 'underground',
                is_punch: 1,
                is_cover_sewer: 1,
                builder_date: coverSewerRequestedDate,
                date_scheduled: coverSewerRequestedDate
            };

            if (teamVal) {
                phaseInsert.date_scheduled = coverSewerRequestedDate;
            }

            insertedPhase = await CentralCalls.upsertData(phaseInsert, 'phase', 'insert');

            const phaseStatusInsert = {
                phase_oid: insertedPhase.oid
            };

            if (teamVal) {
                phaseStatusInsert.employee_team_oid = teamVal;
                phaseStatusInsert.comments = 'Cover Sewer';
            }

            await insertPhaseStatusWithRecovery(phaseStatusInsert);

            if (selectedFiles.length > 0 && row?.phase_oid) {
                try {
                    await uploadFilesForPhase(selectedFiles, row.phase_oid, lotOid);
                } catch (uploadErr) {
                    console.error('Error uploading cover sewer files:', uploadErr);
                    alert(uploadErr?.message || 'Cover Sewer updates saved, but one or more files failed to upload.');
                }
            }

            setCoverSewerDialogOpen(false);
            setCoverSewerRequestedDate('');
            setCoverSewerTeams([]);
            setCoverSewerTeam('');
            coverSewerFilesRef.current?.clearFiles();
            coverSewerHandledTriggerRef.current = null;

            if (onComplete) onComplete();
        } catch (err) {
            console.error('Error submitting Cover Sewer updates:', err);
        }
    };

    const cancelCoverSewer = () => {
        setCoverSewerDialogOpen(false);
        setCoverSewerRequestedDate('');
        setCoverSewerTeam('');
        setCoverSewerTeams([]);
        coverSewerFilesRef.current?.clearFiles();
        coverSewerHandledTriggerRef.current = null;

        if (onComplete) onComplete();
    };

    // Confirm and create the punch phase with collected info
    const confirmFailedQCAutoPunch = async () => {
        const row = failedQCPhaseRow;
        if (!row) return;
        const selectedFiles = failedQCFilesRef.current?.getFiles() || [];

        const punchPhaseType = row.phase_type.split('_')[0];
        const teamVal = failedQCTeamOid ? parseInt(failedQCTeamOid, 10) : null;

        let newPhase = null;
        try {
            if (teamVal) {
                newPhase = await CentralCalls.upsertData(
                    {
                        lot_oid: row.lot_oid,
                        phase_type: punchPhaseType,
                        is_punch: 1,
                        punch_comments: failedQCComments,
                        builder_date: failedQCDate,
                        date_scheduled: failedQCDate
                    },
                    'phase',
                    'insert'
                );
                await insertPhaseStatusWithRecovery(
                    {
                        phase_oid: newPhase.oid,
                        comments: failedQCComments,
                        employee_team_oid: teamVal
                    }
                );
            } else {
                newPhase = await CentralCalls.upsertData(
                    {
                        lot_oid: row.lot_oid,
                        phase_type: punchPhaseType,
                        is_punch: 1,
                        punch_comments: failedQCComments,
                        builder_date: failedQCDate,
                        date_scheduled: failedQCDate
                    },
                    'phase',
                    'insert'
                );
                await insertPhaseStatusWithRecovery(
                    {
                        phase_oid: newPhase.oid,
                        comments: failedQCComments
                    }
                );
            }

            // Create new inspection phase if this is an inspection
            if (failedQCIsInspection) {
                const newInspectionPhase = await CentralCalls.upsertData(
                    {
                        lot_oid: row.lot_oid,
                        phase_type: row.phase_type,
                        is_punch: 1,
                        date_scheduled: failedQCInspectionDate || null,
                        builder_date: failedQCInspectionDate || null,
                        qc_phase_oid: newPhase.oid
                    },
                    'phase',
                    'insert'
                );
                await insertPhaseStatusWithRecovery(
                    {
                        phase_oid: newInspectionPhase.oid
                        //comments: failedQCComments
                    }
                );
            }

            // Create PO if provided
            if (failedQCPoNumber || (failedQCCost && failedQCCost > 0)) {
                await CentralCalls.upsertData(
                    {
                        phase_oid: newPhase.oid,
                        po_number: failedQCPoNumber,
                        price: failedQCCost ? parseFloat(failedQCCost) : null,
                        phase_type: punchPhaseType,
                        comments: failedQCComments
                    },
                    'po',
                    'insert'
                );
            }

            if (selectedFiles.length > 0 && row?.phase_oid && row?.lot_oid) {
                try {
                    await uploadFilesForPhase(selectedFiles, row.phase_oid, row.lot_oid);
                } catch (uploadErr) {
                    console.error('Error uploading failed QC files:', uploadErr);
                    alert(uploadErr?.message || 'Punch created, but one or more files failed to upload.');
                }
            }

            // Close and cleanup
            setFailedQCDialogOpen(false);
            setFailedQCPhaseRow(null);
            setFailedQCComments('');
            setFailedQCDate('');
            setFailedQCTeamOid('');
            setFailedQCTeams([]);
            setFailedQCPoNumber('');
            setFailedQCCost('');
            setFailedQCIsInspection(false);
            setFailedQCInspectionDate('');
            failedQCFilesRef.current?.clearFiles();

            if (onComplete) onComplete();
        } catch (err) {
            console.error('Error in confirmFailedQCAutoPunch:', err);
        }
    };

    // Main routing logic when component opens with a row
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!open || !row) return;

        // Always update slab location first, regardless of route
        updateSlabLocation();

        // Route 1: Underground inspection passed -> create Cover Sewer punch phase/status, then collect requested date/team
        console.log('checking for cover sewer conditions...', row);
        if (row.is_inspection === 1 && row.qc_status === 'success' && row.phase_type === 'underground_inspection') {
            const triggerKey = `${row.phase_oid || row.oid || 'na'}:${row.qc_status}:${row.phase_type}`;
            // console.log('[CoverSewer] Trigger check', {
            //     triggerKey,
            //     inFlight: coverSewerInFlightRef.current,
            //     dialogOpen: coverSewerDialogOpen,
            //     handledTriggerKey: coverSewerHandledTriggerRef.current
            // });
            if (
                !coverSewerInFlightRef.current &&
                !coverSewerDialogOpen &&
                coverSewerHandledTriggerRef.current !== triggerKey
            ) {
                coverSewerHandledTriggerRef.current = triggerKey;
                console.log('[CoverSewer] Trigger accepted, opening cover sewer dialog', { triggerKey });
                openCoverSewerDialog();
            } else {
                console.log('[CoverSewer] Trigger skipped by guard', {
                    triggerKey,
                    inFlight: coverSewerInFlightRef.current,
                    dialogOpen: coverSewerDialogOpen,
                    handledTriggerKey: coverSewerHandledTriggerRef.current
                });
            }
        }

        // Route 2: QC or Inspection phase -> use existing handlePostFinishLogic
        else if (row.is_qc === 1 || row.is_inspection === 1) {
            handlePostFinishLogic();
        }
        // Route 3: Finished failed phase (not QC/Inspection) -> open dialog to collect additional info
        else if (row.finished === 1 && row.status === 'fail' && row.is_qc !== 1 && row.is_inspection !== 1) {
            confirmNewPhaseStatus(row);
        }
        // Route 4: All other cases -> complete immediately without any dialog
        else {
            if (onComplete) onComplete();
        }
    }, [open, row]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) {
            coverSewerInFlightRef.current = false;
            coverSewerHandledTriggerRef.current = null;
        }
    }, [open]);

    return (
        <>
            {/* Failed QC -> create punch confirmation dialog */}
            <Dialog
                fullWidth={true}
                maxWidth='sm'
                open={failedQCDialogOpen}
                onClose={(e, reason) => {
                    // prevent closing via backdrop click or escape key — only OK can close
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    setFailedQCDialogOpen(false);
                }}>
                <DialogTitle>Failed QC - Create Punch (additional info)</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Provide any comments, a requested date, and assign a team for the auto-created punch phase.
                    </DialogContentText>
                    <TextField
                        margin="normal"
                        label="Comments"
                        multiline
                        minRows={3}
                        fullWidth
                        value={failedQCComments}
                        onChange={(e) => setFailedQCComments(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label="Requested Date"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={failedQCDate}
                        onChange={(e) => setFailedQCDate(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label="Assign Team"
                        select
                        fullWidth
                        value={failedQCTeamOid}
                        onChange={(e) => setFailedQCTeamOid(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {failedQCTeams
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((t) => (
                                <MenuItem key={t.oid} value={t.oid}>{t.name}</MenuItem>
                            ))}
                    </TextField>

                    {failedQCIsInspection && (
                        <div style={{ marginTop: 8, marginBottom: 8 }}>
                            <label style={{ display: 'block', fontWeight: 500 }}>New Inspection</label>
                            <TextField
                                margin="normal"
                                label="Date Requested"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={failedQCInspectionDate}
                                onChange={(e) => setFailedQCInspectionDate(e.target.value)}
                            />
                        </div>
                    )}

                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <label style={{ display: 'block', fontWeight: 500 }}>PO</label>
                        <TextField
                            margin="normal"
                            label="PO Number"
                            fullWidth
                            value={failedQCPoNumber}
                            onChange={(e) => setFailedQCPoNumber(e.target.value)}
                        />
                        <TextField
                            margin="normal"
                            label="Cost"
                            type="number"
                            inputProps={{ step: "0.01", min: 0 }}
                            fullWidth
                            value={failedQCCost}
                            onChange={(e) => setFailedQCCost(e.target.value)}
                        />
                    </div>

                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                        <FileUpload
                            ref={failedQCFilesRef}
                            inputId="failed-qc-file-input"
                            title="Files"
                            isUploading={isUploadingFiles}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    {/* No Cancel — only the OK button proceeds */}
                    <Button onClick={confirmFailedQCAutoPunch} color="primary" disabled={isUploadingFiles}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* New Phase Status dialog */}
            <Dialog
                fullWidth={true}
                maxWidth='sm'
                open={newPhaseStatusDialogOpen}
                onClose={() => setNewPhaseStatusDialogOpen(false)}
            >
                <DialogTitle>Add Phase Status</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Provide optional comments, date scheduled, and assign a team for this phase.
                    </DialogContentText>
                    <TextField
                        margin="normal"
                        label="Comments"
                        multiline
                        minRows={3}
                        fullWidth
                        value={newPhaseStatusComments}
                        onChange={(e) => setNewPhaseStatusComments(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label="Date Scheduled"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={newPhaseStatusDateScheduled}
                        onChange={(e) => setNewPhaseStatusDateScheduled(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label="Assign Team"
                        select
                        fullWidth
                        value={newPhaseStatusTeamOid}
                        onChange={(e) => setNewPhaseStatusTeamOid(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {newPhaseStatusTeams
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((t) => (
                                <MenuItem key={t.oid} value={t.oid}>{t.name}</MenuItem>
                            ))}
                    </TextField>
                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            id="putOnHoldCheckbox"
                            checked={newPhaseStatusPutOnHold}
                            onChange={(e) => setNewPhaseStatusPutOnHold(e.target.checked)}
                        />
                        <label htmlFor="putOnHoldCheckbox" style={{ marginLeft: 8, fontWeight: 500 }}>
                            Put On Hold
                        </label>
                    </div>

                    <div style={{ marginTop: 12, marginBottom: 8 }}>
                        <FileUpload
                            ref={newPhaseStatusFilesRef}
                            inputId="new-phase-status-file-input"
                            title="Files"
                            isUploading={isUploadingFiles}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewPhaseStatusDialogOpen(false)} color="secondary">Cancel</Button>
                    <Button onClick={submitNewPhaseStatus} color="primary" disabled={isUploadingFiles}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* Cover Sewer dialog */}
            <Dialog
                fullWidth={true}
                maxWidth='sm'
                open={coverSewerDialogOpen}
                onClose={(e, reason) => {
                    // prevent closing via backdrop click or escape key — only OK can close
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    setCoverSewerDialogOpen(false);
                }}
            >
                <DialogTitle>Cover Sewer</DialogTitle>
                <DialogContent>
                   
                    <TextField
                        margin="normal"
                        label="Requested Date"
                        type="date"
                        required
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={coverSewerRequestedDate}
                        onChange={(e) => setCoverSewerRequestedDate(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        label="Assign Team"
                        select
                        fullWidth
                        value={coverSewerTeam}
                        onChange={(e) => setCoverSewerTeam(e.target.value)}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {coverSewerTeams
                            .slice()
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((t) => (
                                <MenuItem key={t.oid} value={t.oid}>{t.name}</MenuItem>
                            ))}
                    </TextField>

                    <div style={{ marginTop: 12, marginBottom: 8 }}>
                        <FileUpload
                            ref={coverSewerFilesRef}
                            inputId="cover-sewer-file-input"
                            title="Files"
                            isUploading={isUploadingFiles}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelCoverSewer} color="secondary">Cancel</Button>
                    <Button onClick={submitCoverSewer} color="primary" disabled={!coverSewerRequestedDate || isUploadingFiles}>OK</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PostFinishJobAutomation;
